import React, { useCallback, useMemo } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Paginator from '@components/common/pagination/Paginator';
import ErrorPage from '@components/common/Error';
import LoadingPage from '@components/common/Loading';
import { usePaginatedQuery } from '@components/common/pagination/usePaginatedQuery';
import { useAsync } from '@util/customhooks/useAsync';
import { StudentData } from '@ypp-portal/models/user/studentData';
import { LRU } from '@util/LRU';
import { UserData } from '@ypp-portal/models/user/userData';
import PageHeader from '@components/common/PageHeader';
import DataTable from '@components/common/table/DataTable';
import DataTableRow from '@components/common/table/DataTableRow';
import StringDisplay from '@components/common/table/static/StringDisplay';
import { useEmails } from '@components/admin/records/useEmails';

const StudentRecords: React.FC = () => {
  const numStudents = 25;

  const baseQuery = firebase
    .firestore()
    .collectionGroup('students')
    .where('enrollment.session10', '!=', []) as firebase.firestore.Query<StudentData>;

  const { result, next, previous, hasNext, hasPrevious, loading, error } =
    usePaginatedQuery<StudentData>(baseQuery, numStudents);

  // We will at most have numStudents parents (if no students share parents)
  // This is reset
  const parentLru = useMemo(() => new LRU<string, UserData>(numStudents), []);

  const getParents = useCallback(async () => {
    if (!result) {
      return;
    }

    const promises = result.docs.map(async (x) => {
      const parent = x.ref.parent.parent;
      if (!parent) {
        return null;
      }

      const cached = parentLru.get(parent.id);
      if (cached) {
        return { studentId: x.id, parentId: parent.id, parent: cached };
      }

      const doc = await parent.get();

      return { studentId: x.id, parentId: parent.id, parent: doc.data() as UserData | undefined };
    });

    return await Promise.all(promises);
  }, [result, parentLru]);

  const [parents, parentsLoading, parentsError] = useAsync(getParents);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ids = useMemo(() => result?.docs.map((x) => x.ref.parent.parent!.id), [result]);

  const [emails, emailsLoading, emailsError] = useEmails(ids, 'getEmailsFromParentIds');

  if (loading || emailsLoading || parentsLoading) {
    return <LoadingPage></LoadingPage>;
  }

  if (error || emailsError || parentsError || !result || !emails || !parents) {
    return <ErrorPage></ErrorPage>;
  }

  return (
    <div className='w-full h-full overflow-auto'>
      <PageHeader text='STUDENT RECORDS'></PageHeader>
      <DataTable
        headers={[
          'Student First Name',
          'Student Last Name',
          'Student Email',
          'Parent First Name',
          'Parent Last Name',
          'Parent Email',
        ]}
      >
        {result.docs.map((doc) => {
          const docData = doc.data();
          const parentData = parents.find((x) => x?.studentId === doc.id);
          const parent = parentData?.parent;
          const parentEmail = emails.find((x) => x.id === parentData?.parentId);

          return (
            <DataTableRow key={doc.id}>
              <StringDisplay value={docData.static.firstName}></StringDisplay>
              <StringDisplay value={docData.static.lastName}></StringDisplay>
              <StringDisplay value={docData.static.email}></StringDisplay>
              <StringDisplay value={parent?.static.firstName ?? 'N/A'}></StringDisplay>
              <StringDisplay value={parent?.static.lastName ?? 'N/A'}></StringDisplay>
              <StringDisplay value={parentEmail?.email ?? 'N/A'}></StringDisplay>
            </DataTableRow>
          );
        })}
      </DataTable>
      <Paginator onNext={next} onPrevious={previous} hasNext={hasNext} hasPrevious={hasPrevious} />
    </div>
  );
};

export default StudentRecords;
